import { useState, useEffect } from 'react';
import { Prompt, useLocation } from 'react-router-dom';
import tinycolor from 'tinycolor2';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';

import Dropzone from 'components/Signup/Dropzone';
import ColorPicker from 'components/ColorPicker/ColorPicker';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import services from 'services';
import { configurableThemeColors, defaultChartColors } from 'config/constants';
import { ThemePreview } from './ThemePreview';

import styles from './theme-switcher.scss';
import { InputAdornment, TextField } from '@mui/material';
import { Height } from '@mui/icons-material';

export const ThemeSwitcher = () => {
  // theme saved at the backend (needed in order to discard changes)
  const [savedTheme, setSavedTheme] = useState(null);

  // current theme as shown in the UI components
  const [currentTheme, setCurrentTheme] = useState(null);
  const isLoading = currentTheme === null;

  // separate UI component state for drop zones: this is not equal to the state in currentTheme as we only want to
  // show a file if the user has uploaded one and clear it after saving
  const [logoUploadFull, setLogoUploadFull] = useState([]);
  const [logoUploadShort, setLogoUploadShort] = useState([]);

  // confirmation dialog for theme reset
  const [dialogResetOpen, setDialogResetOpen] = useState(false);

  const location = useLocation();

  const hasColorChanges =
    Object.keys(configurableThemeColors).some(color => savedTheme?.cssVars[color] !== currentTheme?.cssVars[color]) ||
    savedTheme?.cssVars['--chart-colors'] !== currentTheme?.cssVars['--chart-colors'];
  const hasLogoFullChanges = savedTheme?.assets?.logoFull !== currentTheme?.assets?.logoFull;
  const hasLogoShortChanges = savedTheme?.assets?.logoShort !== currentTheme?.assets?.logoShort;
  const hasLogoHeightChanges = savedTheme?.cssVars['--max-logo-height'] !== currentTheme?.cssVars['--max-logo-height'];

  const { withLoader } = useLoader();
  const { errorMessage, successMessage } = useMessage();

  const handleColorPickerChange = (color, newColor) => {
    let updatedTheme = {
      ...currentTheme,
      cssVars: {
        ...currentTheme.cssVars,
        [color]: newColor
      }
    };
    if (color === '--follow-up-color') {
      updatedTheme = computeAndAddDerivedFollowUpColors(updatedTheme);
    }
    setCurrentTheme(updatedTheme);
  };

  const handleMaxLogoHeightChange = event => {
    const newValue = event.target.value;
    setCurrentTheme({
      ...currentTheme,
      cssVars: {
        ...currentTheme.cssVars,
        ['--max-logo-height']: `${newValue}px`
      }
    });
  };

  const handleChartColorPickerChange = (index, newColor) => {
    const updatedChartColors = [...currentTheme.cssVars['--chart-colors']]; // make a copy to not mutate the state
    updatedChartColors[index] = newColor;
    const updatedTheme = {
      ...currentTheme,
      cssVars: {
        ...currentTheme.cssVars,
        ['--chart-colors']: updatedChartColors
      }
    };
    setCurrentTheme(updatedTheme);
  };

  const getPresetChartColor = index => {
    // cycle through the default chart colors
    return defaultChartColors[index % defaultChartColors.length];
  };

  const addChartColor = () => {
    const updatedChartColors = [...currentTheme.cssVars['--chart-colors']]; // make a copy to not mutate the state
    updatedChartColors.push(getPresetChartColor(updatedChartColors.length));
    const updatedTheme = {
      ...currentTheme,
      cssVars: {
        ...currentTheme.cssVars,
        ['--chart-colors']: updatedChartColors
      }
    };
    setCurrentTheme(updatedTheme);
  };

  const removeChartColor = () => {
    const updatedChartColors = [...currentTheme.cssVars['--chart-colors']]; // make a copy to not mutate the state
    updatedChartColors.pop();
    const updatedTheme = {
      ...currentTheme,
      cssVars: {
        ...currentTheme.cssVars,
        ['--chart-colors']: updatedChartColors
      }
    };
    setCurrentTheme(updatedTheme);
  };

  const handleFullImageDrop = files => {
    setLogoUploadFull(files);
    const logo = files[0].data;
    const updatedTheme = {
      ...currentTheme,
      assets: { ...currentTheme.assets, logoFull: logo }
    };
    setCurrentTheme(updatedTheme);
  };

  const handleShortImageDrop = files => {
    setLogoUploadShort(files);
    const logo = files[0].data;
    const updatedTheme = {
      ...currentTheme,
      assets: { ...currentTheme.assets, logoShort: logo }
    };
    setCurrentTheme(updatedTheme);
  };

  const handleFullImageDelete = () => {
    setLogoUploadFull([]);
    const updatedTheme = {
      ...currentTheme,
      assets: { ...currentTheme.assets, logoFull: savedTheme.assets.logoFull }
    };
    setCurrentTheme(updatedTheme);
  };

  const handleShortImageDelete = () => {
    setLogoUploadShort([]);
    const updatedTheme = {
      ...currentTheme,
      assets: { ...currentTheme.assets, logoShort: savedTheme.assets.logoShort }
    };
    setCurrentTheme(updatedTheme);
  };

  const loadTheme = async () => {
    const result = await withLoader(services.getThemes());
    if (!result.success) {
      errorMessage(result.error?.message || 'There was an error loading the current theme.');
      return false;
    }

    // only support editing the default theme
    const theme = result.data?.default || null;
    Object.keys(configurableThemeColors).forEach(color => {
      if (!theme?.cssVars[color]) {
        // get default color configured as a root CSS variable in styles/constants.css
        theme.cssVars[color] = getComputedStyle(document.documentElement).getPropertyValue(color);
      }
    });

    // if no chart colors are configured, set the primary color as the first chart color
    if (!theme?.cssVars['--chart-colors']) {
      theme.cssVars['--chart-colors'] = [theme.cssVars['--primary-color']];
    }

    setSavedTheme(theme);
    setCurrentTheme(theme);
    return true;
  };

  // These transformations are based on the original follow-up color in constants.css to get a dark and light variant
  // as hardcoded in constants.css
  const computeAndAddDerivedFollowUpColors = theme => {
    const followUpColor = tinycolor(theme.cssVars['--follow-up-color']);
    const followUpColorDark = followUpColor
      .clone()
      .spin(7)
      .saturate(4)
      .darken(24);
    const followUpColorLight = followUpColor
      .clone()
      .desaturate(2)
      .lighten(25)
      .spin(1);
    const newTheme = { ...theme };
    newTheme.cssVars['--follow-up-color-dark'] = followUpColorDark.toHexString();
    newTheme.cssVars['--follow-up-color-light'] = followUpColorLight.toHexString();
    return newTheme;
  };

  const computeAndAddDerivedColors = theme => {
    const primaryColor = tinycolor(theme.cssVars['--primary-color']);

    const primaryDarkColor = primaryColor.clone().darken(15);
    const primaryLightColor = primaryColor.clone().lighten(10);
    let newTheme = { ...theme };

    // NOTE: this overrides any custom changes made to these three theme variables
    newTheme.cssVars['--primary-dark-color'] = primaryDarkColor.toHexString();
    newTheme.cssVars['--primary-light-color'] = primaryLightColor.toHexString();
    newTheme.cssVars['--kg-color-selected'] = primaryDarkColor.toHexString();

    // also compute and add derived follow-up colors
    newTheme = computeAndAddDerivedFollowUpColors(newTheme);

    return newTheme;
  };

  const saveTheme = async () => {
    // only support editing the default theme
    // NOTE: this overrides any additionally configured themes
    const updatedThemes = {
      default: computeAndAddDerivedColors(currentTheme)
    };

    const result = await services.saveThemes(updatedThemes);
    if (!result.success) {
      return errorMessage(result.error?.message || 'There was an error saving the theme.');
    }
    successMessage('Theme updated successfully!');

    setSavedTheme(currentTheme);
    setLogoUploadFull([]);
    setLogoUploadShort([]);
  };

  const resetTheme = async () => {
    setDialogResetOpen(false);
    const result = await services.deleteThemes();
    if (!result.success) {
      return errorMessage('There was an error while resetting the theme.');
    }
    if (await loadTheme()) {
      successMessage('Theme reset successfully!');
    }
  };

  const discardChanges = () => {
    setCurrentTheme(savedTheme);
    setLogoUploadFull([]);
    setLogoUploadShort([]);
  };

  useEffect(() => {
    loadTheme();
  }, []);

  let mainButtons;
  if (hasLogoFullChanges || hasLogoShortChanges || hasColorChanges || hasLogoHeightChanges) {
    mainButtons = (
      <div className={styles.buttons}>
        <Button className={styles.rightButton} color="primary" onClick={discardChanges}>
          Discard Changes
        </Button>
        <Button className={styles.rightButton} color="primary" variant="contained" onClick={saveTheme}>
          Save
        </Button>
      </div>
    );
  } else {
    mainButtons = (
      <div className={styles.buttons}>
        <Button
          className={styles.rightButton}
          color="primary"
          disabled={isLoading}
          onClick={() => setDialogResetOpen(true)}
        >
          Reset to Defaults
        </Button>
      </div>
    );
  }

  let chartColorButtons;
  if (isLoading) {
    chartColorButtons = <ColorPicker disabled={true} key="chart-color-1" label="Chart Color 1" />;
  } else {
    const chartColors = currentTheme?.cssVars['--chart-colors'];
    chartColorButtons = chartColors.map((colorHex, index) => (
      <div key={`chart-color-${index}`} className={styles.chartColorContainer}>
        {index > 0 && index === chartColors.length - 1 && (
          // display a remove button for the last chart color, except if there is only one chart color
          <button className={styles.chartColorRemoveButton} onClick={removeChartColor}>
            <Delete color="primary" />
          </button>
        )}
        <ColorPicker
          disabled={isLoading}
          label={`Chart Color ${index + 1}`}
          color={colorHex}
          setColor={newColor => handleChartColorPickerChange(index, newColor)}
        />
      </div>
    ));
  }

  return (
    <div>
      <div className={styles.components}>
        <div className={styles.sectionHeader}>
          <span>Preview</span>
        </div>
        <div className={styles.previewContainer}>
          <ThemePreview className="themePreview" disabled={isLoading} theme={currentTheme} />
        </div>
        <div className={styles.sectionHeader}>
          <span>UI Element Colors & Sizes</span>
        </div>
        <div className={styles.colorPickerContainer}>
          {Object.keys(configurableThemeColors).map(color => (
            <ColorPicker
              disabled={isLoading}
              key={color}
              label={configurableThemeColors[color].label}
              color={currentTheme?.cssVars[color]}
              setColor={newColor => handleColorPickerChange(color, newColor)}
            />
          ))}
          <TextField
            disabled={isLoading}
            label="Max Logo Height (px)"
            type="number"
            value={
              currentTheme?.cssVars['--max-logo-height'] ? parseInt(currentTheme.cssVars['--max-logo-height']) : 22
            }
            onChange={handleMaxLogoHeightChange}
            size="small"
            InputProps={{
              inputProps: { min: 1 },
              startAdornment: (
                <InputAdornment position="start" sx={{ padding: '0' }}>
                  <Height fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white'
              },
              '& .MuiOutlinedInput-input': {
                padding: '14.5px 0px',
                paddingRight: '10px' // make the ^ v buttons not stick too close to the border
              }
            }}
          />
        </div>
        <div className={styles.sectionHeader}>
          <span>Chart Colors</span>
        </div>
        <div className={styles.colorPickerContainer}>
          {chartColorButtons}
          <div>
            <Button className={styles.addButton} startIcon={<Add />} color="primary" onClick={addChartColor}>
              Add
            </Button>
          </div>
        </div>
        <div className={styles.sectionHeader}>
          <span>Logos</span>
        </div>
        <div className={styles.dropzoneContainer}>
          <div className={styles.dropzone}>
            <Dropzone
              fullHeight
              fileType="image"
              maxFileSizeInBytes={1048580}
              dropzoneText={logoUploadFull.length < 1 ? 'Main Logo' : logoUploadFull[0].file.name}
              filesLimit={1}
              fileObjects={logoUploadFull}
              onAdd={handleFullImageDrop}
              onDelete={handleFullImageDelete}
              showPreviewsInDropzone={true}
            />
          </div>
          <div className={styles.dropzone}>
            <Dropzone
              fullHeight
              fileType="image"
              maxFileSizeInBytes={1048580}
              dropzoneText={logoUploadShort.length < 1 ? 'Chat Icon' : logoUploadShort[0].file.name}
              filesLimit={1}
              fileObjects={logoUploadShort}
              onAdd={handleShortImageDrop}
              onDelete={handleShortImageDelete}
              showPreviewsInDropzone={true}
            />
          </div>
        </div>
      </div>

      {mainButtons}

      <Prompt
        message={newLocation => {
          if (
            (hasLogoFullChanges || hasLogoShortChanges || hasColorChanges || hasLogoHeightChanges) &&
            location.pathname !== newLocation.pathname
          ) {
            return 'You have unsaved modifications which will be lost unless you save them. Are you sure you want to leave?';
          }
          return true;
        }}
      />

      <Dialog maxWidth="xs" fullWidth open={dialogResetOpen} onClose={() => setDialogResetOpen(false)}>
        <DialogTitle>Reset Theme</DialogTitle>
        <DialogContent>Do you really want to reset the theme to the default theme?</DialogContent>
        <DialogActions>
          <Button autoFocus color="primary" disabled={isLoading} onClick={() => setDialogResetOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" disabled={isLoading} onClick={resetTheme}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
